import { useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import { captureException } from '@sentry/react';
import { useClientUser } from '@/hooks';
import { CYCLE_COUNTS_LIST, CYCLE_COUNT_LIST_INVENTORY } from './graphql/queries';
import { enrichCycleCountsWithExpectedItems, filterCycleCounts } from './utils';

export function useCycleCountsData() {
    const { client_id } = useClientUser();

    const { data: listData, loading: listLoading, refetch: refetchCycleCounts } = useQuery(CYCLE_COUNTS_LIST, {
        variables: { where: { client_id: { _eq: client_id } } },
        skip: !client_id,
        fetchPolicy: 'cache-and-network',
        onError: (error) => {
            captureException(error);
        },
    });

    const cycleCounts = listData?.cycle_counts;

    const warehouseIds = useMemo(() => {
        const ids = new Set();
        (cycleCounts || []).forEach((count) => {
            if (count?.warehouse_id) ids.add(count.warehouse_id);
        });
        return Array.from(ids);
    }, [cycleCounts]);

    const { data: inventoryData, loading: inventoryLoading } = useQuery(CYCLE_COUNT_LIST_INVENTORY, {
        variables: { warehouse_ids: warehouseIds },
        skip: !client_id || warehouseIds.length === 0,
        fetchPolicy: 'cache-and-network',
        onError: (error) => {
            captureException(error);
        },
    });

    const enrichedCounts = useMemo(() => {
        if (!cycleCounts) return undefined;
        return enrichCycleCountsWithExpectedItems(cycleCounts, inventoryData?.items);
    }, [cycleCounts, inventoryData?.items]);

    const loading = listLoading || (warehouseIds.length > 0 && inventoryLoading);

    return {
        cycleCounts: enrichedCounts,
        inventoryItems: inventoryData?.items,
        loading,
        refetchCycleCounts,
    };
}

// Owns the list page's filter state and derived data so the view stays presentational.
export function useCycleCountsList() {
    const { cycleCounts, inventoryItems, loading, refetchCycleCounts } = useCycleCountsData();
    const [searchTerm, setSearchTerm] = useState('');
    const [warehouseFilter, setWarehouseFilter] = useState('');
    const [dateFromFilter, setDateFromFilter] = useState(null);

    const warehouseFilterOptions = useMemo(() => {
        const byId = new Map();
        (cycleCounts || []).forEach((count) => {
            if (count?.warehouse?.location_id && !byId.has(count.warehouse.location_id)) {
                byId.set(count.warehouse.location_id, count.warehouse);
            }
        });
        return Array.from(byId.values()).sort((a, b) =>
            (a.location_name || '').localeCompare(b.location_name || '')
        );
    }, [cycleCounts]);

    const filteredCycleCounts = useMemo(
        () =>
            filterCycleCounts(cycleCounts, inventoryItems, {
                searchTerm,
                warehouseId: warehouseFilter || null,
                dateFrom: dateFromFilter,
            }),
        [cycleCounts, inventoryItems, searchTerm, warehouseFilter, dateFromFilter]
    );

    return {
        filteredCycleCounts,
        warehouseFilterOptions,
        showSpinner: loading && (!cycleCounts || cycleCounts.length === 0),
        refetchCycleCounts,
        searchTerm,
        setSearchTerm,
        warehouseFilter,
        setWarehouseFilter,
        dateFromFilter,
        setDateFromFilter,
    };
}
